import { observer } from 'mobx-react-lite';
import type React from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@workspace/ui/components/Button';
import { cn } from '@workspace/ui/lib/utils';

import Icon from '~/components/Icon/Icon';
import { ProductListAnalyticsProvider } from '~/components/ProductListAnalytics';
import { searchSuggestProductList } from '~/services/analytics/productLists';
import type { Product } from '~/stores/CategoriesStore';

import { SearchResultItemDropdown } from './SearchResultItemDropdown';

interface SearchResultsSectionProps
  extends Omit<React.ComponentPropsWithoutRef<'div'>, 'children'> {
  searchResults: Product[] | null;
  handleCloseDropdown: (
    e?: React.MouseEvent<HTMLButtonElement | HTMLDivElement>,
  ) => void;
  handleStartSearch?: () => void;
}

export const SearchResultsSection = observer<SearchResultsSectionProps>(
  ({
    searchResults,
    handleCloseDropdown,
    handleStartSearch,
    className,
    ...props
  }) => {
    const { t } = useTranslation();

    if (!searchResults || searchResults.length === 0) {
      return null;
    }

    return (
      <div {...props} className={cn('flex flex-col gap-2', className)}>
        <ProductListAnalyticsProvider list={searchSuggestProductList()}>
          <div className="flex flex-col gap-1">
            {searchResults.map((product) => (
              <SearchResultItemDropdown
                key={product.id}
                product={product}
                onClose={handleCloseDropdown}
              />
            ))}
          </div>
        </ProductListAnalyticsProvider>
        {handleStartSearch && (
          <Button
            variant="brand"
            size="lg"
            endIcon={
              <Icon type="chevron-3.1" size={11} className="rotate-180" />
            }
            className="mx-auto mt-2 mb-6 min-w-[205px] whitespace-nowrap px-5 py-[14px]"
            onClick={(e) => {
              e.stopPropagation();
              handleStartSearch();
            }}
          >
            {t('header:seeAllResults')}
          </Button>
        )}
      </div>
    );
  },
);
